import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import {
  type Language,
  translations,
  getTranslation,
  interpolate,
  getStoredLanguage,
  setStoredLanguage,
  LANGUAGES,
  DEFAULT_LANGUAGE,
} from '@/i18n'

/**
 * I18n Context and Hook
 * Provides translation functionality throughout the app.
 *
 * Usage:
 * const { t, language, setLanguage } = useI18n()
 * t('common.save') // "Save" or "Запази"
 * t('billing.features.rules', { count: 5 }) // "5 rules"
 */

interface I18nContextValue {
  /** Current language */
  language: Language
  /** Set the current language */
  setLanguage: (lang: Language) => void
  /** Translate a key with optional interpolation */
  t: (key: string, variables?: Record<string, string | number>) => string
  /** Available languages */
  languages: typeof LANGUAGES
}

const I18nContext = createContext<I18nContextValue | null>(null)

export interface I18nProviderProps {
  children: ReactNode
  /** Initial language (defaults to stored preference or browser language) */
  initialLanguage?: Language
}

export function I18nProvider({
  children,
  initialLanguage,
}: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(
    initialLanguage ?? getStoredLanguage()
  )

  // Update document lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang)
    setStoredLanguage(lang)
  }, [])

  const t = useCallback(
    (key: string, variables?: Record<string, string | number>): string => {
      const translation = getTranslation(translations[language], key)
      if (variables) {
        return interpolate(translation, variables)
      }
      return translation
    },
    [language]
  )

  const value: I18nContextValue = {
    language,
    setLanguage,
    t,
    languages: LANGUAGES,
  }

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  )
}

/**
 * Hook to access translations
 */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext)

  if (!context) {
    // Return a fallback for use outside provider (e.g., in tests)
    return {
      language: DEFAULT_LANGUAGE,
      setLanguage: () => {
        console.warn('useI18n: I18nProvider not found')
      },
      t: (key: string) => key,
      languages: LANGUAGES,
    }
  }

  return context
}

/**
 * Hook to get just the translation function
 * Slightly more efficient when you don't need language switching
 */
export function useTranslation() {
  const { t, language } = useI18n()
  return { t, language }
}
